import { Box, Icon, InputWrapper, PrimaryButton, TextInput } from '@wrisk/ui-components'
import React, { ChangeEvent, FunctionComponent, useCallback, useState } from 'react'

import { VehicleKBA } from '../../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../../hooks/auth'
import { useHelpLink } from '../../../../../hooks/navigation'
import {
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { useEnterAction } from '../../../../../util/useEnterAction'
import { usePrincipal } from '../../../../authentication'
import { HelpFlexAnchor } from '../../../links'

export interface VehicleLookupInputProps {
  name: string
  value: VehicleKBA | null
  onChange: (value: VehicleKBA[]) => void
  config?: Partial<{
    profile: string
  }>
  tKey: TKeyBuilder
}

export const VehicleLookupInput: FunctionComponent<VehicleLookupInputProps> = ({
  name,
  tKey,
  value,
  onChange,
  config,
}) => {
  const { t } = useWriskTranslation()

  const { apiClient } = usePrincipal()

  const [hsn, setHsn] = useState<string>(value?.hsn ?? '')
  const [tsn, setTsn] = useState<string>(value?.tsn ?? '')

  const helpLink = useHelpLink(name)

  const handleHsnChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setHsn(
      e.target.value
        .toUpperCase()
        .replace(/[^0-9]/g, '')
        .slice(0, 4),
    )
  }, [])

  const handleTsnChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setTsn(
      e.target.value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 3),
    )
  }, [])

  const { execute, loading } = useApiErrorHandlingAsyncCallback(async () => {
    onChange(await apiClient.lookupVehicleSearch({ hsn, tsn }, config?.profile))
  })

  const handleEnter = useEnterAction(execute)

  const isValid = hsn.length === 4 && tsn.length >= 3

  return (
    <Box display='flex' columnGap={3}>
      <Box display='flex' columnGap={3} minWidth={0}>
        <InputWrapper pr={[2, 2, 2, 2]} minWidth={0}>
          <TextInput
            type='tel'
            minWidth={0}
            name={`${name}_hsn`}
            variant='ghost'
            placeholder={t(tKey('placeholders', 'hsn'))}
            onChange={handleHsnChange}
            onKeyDown={handleEnter}
            value={hsn}
          />
          <HelpFlexAnchor
            variant='highlight'
            borderRadius='50%'
            justifyContent='center'
            p={1}
            href={helpLink}
          >
            <Icon icon='help' size='small' />
          </HelpFlexAnchor>
        </InputWrapper>
        <InputWrapper pr={[2, 2, 2, 2]} minWidth={0}>
          <TextInput
            minWidth={0}
            name={`${name}_tsn`}
            variant='ghost'
            placeholder={t(tKey('placeholders', 'tsn'))}
            onChange={handleTsnChange}
            onKeyDown={handleEnter}
            value={tsn}
          />
          <HelpFlexAnchor
            variant='highlight'
            borderRadius='50%'
            justifyContent='center'
            p={1}
            href={helpLink}
          >
            <Icon icon='help' size='small' />
          </HelpFlexAnchor>
        </InputWrapper>
      </Box>

      <PrimaryButton loading={loading} disabled={!isValid} onClick={execute}>
        {t(tKey('actions', 'search'))}
      </PrimaryButton>
    </Box>
  )
}
